import { getToken } from "firebase/app-check"
import { appCheck } from "@/lib/firebase"

// Why a request went out without a token. The backend logs it with the
// unverified-request warning: a request with no token otherwise looks the same
// whether reCAPTCHA was blocked, App Check throttled us or the network dropped,
// and that difference decides whether enforcement would lock a real user out.
const ERROR_HEADER = "X-AppCheck-Error"

// Error codes look like "appCheck/recaptcha-error"; anything else is trimmed to
// that shape so it stays a harmless header value.
const errorCode = (err) =>
  String(err?.code ?? err?.name ?? "unknown")
    .replace(/[^\w/.-]/g, "")
    .slice(0, 64) || "unknown"

/**
 * App Check header for hand-rolled fetch calls.
 *
 * Callables (httpsCallable) attach this automatically once App Check is
 * initialized; onRequest endpoints do not, so every raw fetch has to add it.
 *
 * Never throws when a token can't be minted -- App Check is unenforced today,
 * so a failure here should degrade to an unverified request rather than break
 * the call. Once enforcement is on, the backend rejects the tokenless request,
 * which is the behaviour we want.
 */
export async function appCheckHeaders() {
  if (!appCheck) return { [ERROR_HEADER]: "not-initialized" }
  try {
    const { token } = await getToken(appCheck)
    return { "X-Firebase-AppCheck": token }
  } catch (err) {
    if (import.meta.env.DEV) console.warn("App Check token unavailable:", err)
    return { [ERROR_HEADER]: errorCode(err) }
  }
}
